import React, { useRef, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FlexView, Text, Button, COLORS } from '@cuemath/comps';
import { useGetRegionalTrialSlots } from 'actions/regional-trial-slots';
import BallLoader from 'comps/ball-loader';
import { getFromToTime, transformRegionalSlotsData } from './utils';
import SlotContainer from './slots-container';

const defaultState = {
  date: '',
  time: '',
  timezone: '',
};

const LoadingContainer = styled(FlexView)`
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const SlotPage = ({
  ecnaExperiments,
  height,
  borderRadius,
  showFillingFast,
  minWidth,
  padding,
  opportunityRegion,
  timezone: userTimezone,
  updateStateData,
  bufferHours,
}) => {
  const [state, setState] = useState({
    ...defaultState,
    timezone: userTimezone,
  });
  const [slotsData, setSlotsData] = useState([]);
  const {
    data: regionalSlotsData,
    isProcessing: isRegionalSlotsProcessing,
    error,
    get: getRegionalTrialSlots,
  } = useGetRegionalTrialSlots();

  useEffect(() => {
    if (userTimezone && opportunityRegion) {
      getRegionalTrialSlots(userTimezone, opportunityRegion, bufferHours);
    }
  }, [bufferHours, getRegionalTrialSlots, opportunityRegion, userTimezone]);

  useEffect(() => {
    if (regionalSlotsData) {
      const slotsDataToTransform = regionalSlotsData.available_slots || regionalSlotsData;
      const transformedSlots = transformRegionalSlotsData(
        slotsDataToTransform,
        userTimezone,
      );

      if (transformedSlots.slots.length > 0) {
        setSlotsData(transformedSlots);
      }
    }
  }, [regionalSlotsData, userTimezone]);

  const { ecnaLayoutVariant } = ecnaExperiments || {};
  const isEcnaLayoutV2 = useMemo(() => ecnaLayoutVariant === 'v2', [ecnaLayoutVariant]);
  const timeSlotRef = useRef(null);

  const handleSetState = (stateType, value) => {
    if (stateType.includes('date')) {
      const prefix = stateType.split('date')[0];
      const newData = {
        ...state,
        [stateType]: value,
        [`${prefix}time`]: '',
      };
      updateStateData(newData);

      setState(newData);
    } else if (stateType.includes('timezone')) {
      const newData = { ...state, [stateType]: value };
      updateStateData(newData);
      setState(newData);
    } else {
      const newData = { ...state, [stateType]: getFromToTime(value, 'singleSlots') };
      updateStateData(newData);
      setState(newData);
    }
  };

  const handleRetry = () => {
    if (userTimezone && opportunityRegion) {
      getRegionalTrialSlots(userTimezone, opportunityRegion, bufferHours);
    }
  };

  if (error) {
    return (
      <FlexView
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        minHeight="300px"
        padding="20px"
      >
        <FlexView marginBottom="16px">
          <Text size="16px" color={COLORS.error} textAlign="center">
            Failed to load available slots. Please try again.
          </Text>
        </FlexView>
        {error.message && (
          <FlexView marginBottom="16px">
            <Text size="14px" color={COLORS.grey6} textAlign="center">
              {error.message}
            </Text>
          </FlexView>
        )}
        <Button onClick={handleRetry}>Retry</Button>
      </FlexView>
    );
  }

  if (isRegionalSlotsProcessing) {
    return (
      <LoadingContainer>
        <BallLoader />
      </LoadingContainer>
    );
  }

  return (
    <SlotContainer
      state={state}
      slotsData={slotsData}
      handleSetState={handleSetState}
      isEcnaLayoutV2={isEcnaLayoutV2}
      height={height}
      borderRadius={borderRadius}
      minWidth={minWidth}
      padding={padding}
      showFillingFast={showFillingFast}
      timeSlotRef={timeSlotRef}
    />
  );
};

SlotPage.defaultProps = {
  ecnaExperiments: {
    isDirectAdmission: false,
    ecnaLayoutVariant: '',
  },
  questionsData: {
    skipText: '',
  },
  isDefaultSlotPick: false,
  handleOnSubmit: null, // Do not change this default value
  isDisabledButton: false,
  showError: false,
  infoRowData: '',
  useTwoSectionLayout: false,
  height: '',
  borderRadius: '',
  minWidth: '',
  padding: '',
  showFillingFast: false,
  isSubmitting: false,
  containerWidth: '',
};

SlotPage.propTypes = {
  ecnaExperiments: PropTypes.shape({
    isDirectAdmission: PropTypes.bool,
    ecnaLayoutVariant: PropTypes.string,
  }),
  questionsData: PropTypes.shape({ slot_pick: PropTypes.shape({}) }),
  isDefaultSlotPick: PropTypes.bool,
  handleOnSubmit: PropTypes.func,
  isDisabledButton: PropTypes.bool,
  showError: PropTypes.bool,
  infoRowData: PropTypes.string,
  useTwoSectionLayout: PropTypes.bool,
  height: PropTypes.string,
  borderRadius: PropTypes.string,
  minWidth: PropTypes.string,
  padding: PropTypes.string,
  showFillingFast: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  containerWidth: PropTypes.string,
};

export default SlotPage;
